import { useState, useEffect } from "react";
import Loader from "components/Loader";
import HelpCard from "components/Cards/HelpCard";
import NoteCard from "components/Cards/NoteCard";
import { Button, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";
import MarkdownRender from "components/MarkdownRender/textBox";
import {
  submitPageLevelContentOutlineRequest,
  submitContentOutlineDraftRequest,
  submitAiOverviewByContentRequest,
} from "services/buddyXApiHubService";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import GuideFlip from "components/FlipPanel/GuideFlip";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [isGeneratingWriter1, setIsGeneratingWriter1] = useState(false);
  const [isGeneratingWriter2, setIsGeneratingWriter2] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showDraftNotification, setShowDraftNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [url, setUrl] = useState("");
  const [primaryKeyword, setPrimaryKeyword] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [wordCount, setWordCount] = useState(800);
  const [contentOutline, setContentOutline] = useState("");
  const [generatedDraft, setGeneratedDraft] = useState("");
  const [originalKeyword, setOriginalKeyword] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showDraftResult, setShowDraftResult] = useState(false);
  const [isGeneratingAiOverview, setIsGeneratingAiOverview] = useState(false);
  const [showAiOverviewNotification, setShowAiOverviewNotification] =
    useState(false);
  const [showAiOverviewResult, setShowAiOverviewResult] = useState(false);
  const [noQueries, setNoQueries] = useState(10);
  const [aiOverviewList, setAiOverviewList] = useState([]);
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);
  const [showGuideFlip, setShowGuideFlip] = useState(false);
  const [formData, setFormData] = useState({
    url: "",
    primary_keyword: "",
    brand: "",
    model: "",
    word_count: 800,
  });
  const [errors, setErrors] = useState({
    url: "",
    primaryKeyword: "",
    brand: "",
    model: "",
    wordCount: "",
    noQueries: "",
  });

  const modelOptions = [
    { value: "writer_1", label: "Writer 1" },
    { value: "writer_2", label: "Writer 2" },
    { value: "writer_3", label: "Writer 3" },
    { value: "writer_4", label: "Writer 4" },
  ];
  const model_action_map = {
    writer_1: "page_level_content_draft",
    writer_2: "page_level_content_draft_advanced",
  };
  const firstLevelTopic = "Content Editor";
  const secoundLevelTopic = "Writer";
  const thirdLevelTopic = "URL-Based Optimisation";

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "url":
        setUrl(value);
        break;
      case "primaryKeyword":
        setPrimaryKeyword(value);
        break;
      case "brand":
        setBrand(value);
        break;
      case "model":
        setModel(value);
        break;
      case "wordCount":
        const wordCountValue = parseInt(value) || 800;
        // Check if user tried to enter more than 1200
        if (wordCountValue > 1200) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            wordCount: "Word count cannot exceed 1,200 words",
          }));
          // Still set to 1200 but show error
          setWordCount(1200);
        } else {
          // Clear error if value is valid
          setErrors((prevErrors) => ({
            ...prevErrors,
            wordCount: "",
          }));
          setWordCount(wordCountValue);
        }
        break;
      case "noQueries":
        const queriesValue = parseInt(value) || 10;
        if (queriesValue > 50) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            noQueries: "Number of queries cannot exceed 50",
          }));
          setNoQueries(50);
        } else if (queriesValue < 1) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            noQueries: "Number of queries must be at least 1",
          }));
          setNoQueries(1);
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            noQueries: "",
          }));
          setNoQueries(queriesValue);
        }
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";

    if (
      name === "url" &&
      !Validator.validateRequired(value, Validator.validateUrl)
    ) {
      error = "Please enter a valid URL";
    } else if (
      name === "primaryKeyword" &&
      !Validator.validateRequired(value, Validator.validateSingleKeyword)
    ) {
      error = "Please enter a primary keyword";
    } else if (
      name === "brand" &&
      !Validator.validateRequired(value, Validator.validateBrandName)
    ) {
      error = "Please enter a brand";
    } else if (
      name === "model" &&
      !Validator.validateRequired(value, Validator.validateSingleKeyword)
    ) {
      error = "Please select a writer model";
    } else if (name === "wordCount") {
      const wordCountValue = parseInt(value) || 800;
      if (wordCountValue > 1200) {
        error = "Word count cannot exceed 1,200 words";
      }
    } else if (name === "noQueries") {
      const queriesValue = parseInt(value) || 10;
      if (queriesValue < 1 || queriesValue > 50) {
        error = "Number of queries must be between 1 and 50";
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setIsResultGot(false);
    setIsGeneratingWriter1(false);
    setIsGeneratingWriter2(false);
    setIsGeneratingAiOverview(false);
    setShowCopyNotification(false);
    setShowDraftNotification(false);
    setShowAiOverviewNotification(false);
    setShowErrorNotification(false);
    setShowInsufficientCredits(false);
    setShowDraftResult(false);
    setShowAiOverviewResult(false);
    setUrl("");
    setPrimaryKeyword("");
    setBrand("");
    setModel("");
    setWordCount(800);
    setNoQueries(10);
    setContentOutline("");
    setGeneratedDraft("");
    setAiOverviewList([]);
    setCopiedRowIndex(null);
    setOriginalKeyword(""); // Clear the stored original keyword
    setInsufficientCreditsData(null);
    setErrorMessage("");
    setErrors({
      url: "",
      primaryKeyword: "",
      brand: "",
      model: "",
      wordCount: "",
      noQueries: "",
    });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      // Call the actual API
      const result = await submitPageLevelContentOutlineRequest(
        formData,
        "page_level_content_outliner",
        handleInsufficientCredits
      );

      setContentOutline(result);
      setOriginalKeyword(primaryKeyword); // Store the keyword used for outline generation
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(
        error.message || "Failed to generate content outline. Please try again."
      );
      setShowErrorNotification(true);
    }
    setIsLoading(false);
  };

  const handleGenerateDraft = async (model) => {
    try {
      // Set the appropriate loading state based on the model
      if (model === "writer_1") {
        setIsGeneratingWriter1(true);
      } else if (model === "writer_2") {
        setIsGeneratingWriter2(true);
      }
      setIsLoading(true);

      // Prepare data for draft generation using the original keyword
      const draftData = {
        seo_outline: contentOutline,
        primary_keyword: originalKeyword,
        model: model,
      };

      console.log("Generating draft with data:", draftData);
      const sampleDraft = await submitContentOutlineDraftRequest(
        draftData,
        model_action_map[model],
        handleInsufficientCredits
      );

      setGeneratedDraft(sampleDraft);
      setShowDraftResult(true);
      setShowDraftNotification(true);

      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setShowDraftNotification(false);
      }, 3000);
    } catch (error) {
      console.error("Draft generation error:", error);
      setErrorMessage(
        error.message || "Failed to generate draft. Please try again."
      );
      setShowErrorNotification(true);
    }

    // Reset all loading states
    setIsGeneratingWriter1(false);
    setIsGeneratingWriter2(false);
    setIsLoading(false);
  };

  const handleGenerateAiOverview = async () => {
    try {
      setIsGeneratingAiOverview(true);
      setIsLoading(true);

      // Prepare data for AI overview generation using the generated draft
      const aiOverviewData = {
        text: generatedDraft,
        no_queries: noQueries,
      };

      const result = await submitAiOverviewByContentRequest(
        aiOverviewData,
        "ai_overview_content",
        handleInsufficientCredits
      );

      setAiOverviewList(result);
      setShowAiOverviewResult(true);
      setShowAiOverviewNotification(true);

      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setShowAiOverviewNotification(false);
      }, 3000);
    } catch (error) {
      console.error("AI overview generation error:", error);
      setErrorMessage(
        error.message || "Failed to generate AI overview. Please try again."
      );
      setShowErrorNotification(true);
    }

    // Reset loading states
    setIsGeneratingAiOverview(false);
    setIsLoading(false);
  };

  useEffect(() => {
    const isUrlValid = Validator.validateRequired(url, Validator.validateUrl);
    const isPrimaryKeywordValid = Validator.validateRequired(
      primaryKeyword,
      Validator.validateSingleKeyword
    );
    const isBrandValid = Validator.validateRequired(
      brand,
      Validator.validateBrandName
    );
    const isModelValid = Validator.validateRequired(
      model,
      Validator.validateSingleKeyword
    );

    const isValid =
      isUrlValid && isPrimaryKeywordValid && isBrandValid && isModelValid;

    if (isValid) {
      setFormData({
        url: url,
        primary_keyword: primaryKeyword,
        brand: brand,
        model: model,
        word_count: wordCount,
      });
    }
    setIsFormValid(isValid);
  }, [url, primaryKeyword, brand, model, wordCount]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showCopyNotification}
        message="Content outline copied!"
        type="success"
        icon="fas fa-check-circle"
        duration={2000}
        onClose={() => setShowCopyNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showDraftNotification}
        message="Draft generation started!"
        type="info"
        icon="fas fa-rocket"
        duration={3000}
        onClose={() => setShowDraftNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showAiOverviewNotification}
        message="AI overview generated successfully!"
        type="success"
        icon="fas fa-lightbulb"
        duration={3000}
        onClose={() => setShowAiOverviewNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />

      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
        {"-"}
        {thirdLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / <b>{secoundLevelTopic}</b> /{" "}
        <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Discover <b>content outlines</b> tailored to your writing needs, helping
        you structure your content effectively and improve readability with
        relevant, well-organized sections for any type of content.
      </p>
      <div className="row">
        <div className="col-12">
          <HelpCard
            topic={"How To Use"}
            description={
              "Content Outline helps you create well-structured content outlines for any type of writing. It suggests relevant sections and subsections based on your topic, aiding in organizing your content effectively and ensuring comprehensive coverage of your subject matter."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate Content Outline</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter the required information to generate a content outline.
            </p>
            <Form.Group>
              <div className="row mt-2">
                <div className="col-md-6 mb-3">
                  <Form.Label>URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="url"
                    placeholder="Enter URL"
                    value={url}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.url}
                    required
                    className="px-2"
                    style={getInputFieldStyle("type6")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.url}
                  </Form.Control.Feedback>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label>Primary Keyword</Form.Label>
                  <Form.Control
                    type="text"
                    name="primaryKeyword"
                    placeholder="Enter primary keyword"
                    value={primaryKeyword}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.primaryKeyword}
                    required
                    className="px-2"
                    style={getInputFieldStyle("type6")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.primaryKeyword}
                  </Form.Control.Feedback>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label>Brand</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    placeholder="Enter brand name"
                    value={brand}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.brand}
                    required
                    className="px-2"
                    style={getInputFieldStyle("type6")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.brand}
                  </Form.Control.Feedback>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label>Writer Model</Form.Label>
                  <Form.Select
                    name="model"
                    value={model}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.model}
                    required
                    className="px-2"
                  >
                    <option value="">Select Writer Model</option>
                    {modelOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.model}
                  </Form.Control.Feedback>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label>Word Count</Form.Label>
                  <Form.Control
                    type="number"
                    name="wordCount"
                    placeholder="Enter word count"
                    value={wordCount}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.wordCount}
                    min="100"
                    max="1200"
                    className="px-2"
                    style={getInputFieldStyle("type6")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.wordCount}
                  </Form.Control.Feedback>
                </div>
                <div className="col-md-6 mb-3 d-flex align-items-end">
                  <div className="d-flex">
                    <Button
                      variant="dark"
                      className="me-2"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle("type6", "primary")}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Generate Outline"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      onClick={handleReset}
                      className="me-2"
                      style={getButtonStyle("type6", "outline")}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowGuideFlip(true)}
                      className="d-flex align-items-center gap-2"
                      style={getButtonStyle("type6", "outline")}
                    >
                      <i className="fas fa-question-circle"></i>
                      How-to Guide
                    </Button>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <small className="text-warning mt-1 d-block">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Word count cannot exceed over 1,200 words
                  </small>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <NoteCard
            topic={"Note"}
            description={
              "Content outline will be generated within 5-7 mins, followed by the content draft"
            }
            image={"/icon/help.svg"}
          />
        </div>
      </div>

      <div className="p-2">
        {isResultGot && originalKeyword && (
          <div>
            <p className="d-flex fw-bold mb-3">
              Content outline for
              <span className="search-item">&nbsp;"{originalKeyword}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && contentOutline && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Generated Content Outline</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(contentOutline);
                  setShowCopyNotification(true);
                  // Auto-hide notification after 2 seconds
                  setTimeout(() => {
                    setShowCopyNotification(false);
                  }, 2000);
                }}
              >
                Copy
              </Button>
            </div>
            <div
              className="border rounded p-3 bg-light"
              style={{
                minHeight: "300px",
                maxHeight: "500px",
                overflowY: "auto",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              <MarkdownRender variant="content">
                {contentOutline}
              </MarkdownRender>
            </div>
            <div className="mt-3">
              <Button
                variant="success"
                onClick={() => handleGenerateDraft("writer_1")}
                disabled={isLoading || isGeneratingWriter1}
                className="me-2"
                style={getButtonStyle('type6', 'primary')}
              >
                {isGeneratingWriter1 ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating...
                  </>
                ) : (
                  "Generate Content Draft"
                )}
              </Button>
              <Button
                variant="success"
                onClick={() => handleGenerateDraft("writer_2")}
                disabled={isLoading || isGeneratingWriter2}
                className="me-2"
                style={getButtonStyle('type6', 'primary')}
              >
                {isGeneratingWriter2 ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating...
                  </>
                ) : (
                  "Generate Content Draft - Advanced"
                )}
              </Button>
              <small className="text-muted">
                This will use the content outline and primary keyword to
                generate a full draft
              </small>
            </div>
          </div>
        )}

        {/* Generated Draft Display */}
        {showDraftResult && generatedDraft && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Generated Draft</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(generatedDraft);
                  setShowCopyNotification(true);
                  // Auto-hide notification after 2 seconds
                  setTimeout(() => {
                    setShowCopyNotification(false);
                  }, 2000);
                }}
              >
                Copy
              </Button>
            </div>
            <div
              className="border rounded p-3 bg-light"
              style={{
                minHeight: "300px",
                maxHeight: "500px",
                overflowY: "auto",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              <MarkdownRender variant="content">
                {generatedDraft}
              </MarkdownRender>
            </div>
          </div>
        )}

        {/* AI Overview Section - Shows after draft is generated */}
        {showDraftResult && generatedDraft && (
          <div className="mb-4">
            <div className="p-3 border rounded bg-light">
              <h5 className="mb-3">Generate AI Overview</h5>
              <p className="text-muted mb-3">
                Generate AI overview queries based on your content draft to
                understand what questions people might ask.
              </p>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label>Number of Queries</Form.Label>
                  <Form.Control
                    type="number"
                    name="noQueries"
                    placeholder="Enter number of queries"
                    value={noQueries}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.noQueries}
                    min="1"
                    max="50"
                    className="px-2"
                    style={getInputFieldStyle("type6")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.noQueries}
                  </Form.Control.Feedback>
                  <small className="text-muted mt-1 d-block">
                    <i className="fas fa-info-circle me-1"></i>
                    Number of queries must be between 1 and 50
                  </small>
                </div>
                <div className="col-md-6 mb-3 d-flex align-items-start">
                  <div className="d-flex flex-column">
                    <div className="mb-2" style={{ height: "24px" }}></div>
                    <Button
                      variant="primary"
                      onClick={handleGenerateAiOverview}
                      disabled={
                        isLoading || isGeneratingAiOverview || !generatedDraft
                      }
                      className="me-2"
                      style={getButtonStyle("type6", "primary")}
                    >
                      {isGeneratingAiOverview ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Generate AI Overview"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Overview Results Display */}
        {showAiOverviewResult &&
          aiOverviewList &&
          aiOverviewList.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-dark">
                  <tr>
                    <th scope="col" style={{ width: "60px", color: "black" }}>
                      #
                    </th>
                    <th scope="col" style={{ color: "black" }}>
                      AI Overview
                    </th>
                    <th scope="col" style={{ width: "100px", color: "black" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {aiOverviewList.map((item, index) => (
                    <tr key={index}>
                      <th scope="row">{index + 1}</th>
                      <td>
                        <div
                          style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                            fontSize: "14px",
                            lineHeight: "1.5",
                          }}
                        >
                          {typeof item === "object" &&
                          item !== null &&
                          !Array.isArray(item) ? (
                            <div>
                              {Object.entries(item).map(
                                ([question, answer], entryIndex) => (
                                  <div key={entryIndex}>
                                    <div
                                      className="text-primary fw-bold mb-2"
                                      style={{ fontSize: "14px" }}
                                    >
                                      {question}
                                    </div>
                                    <div
                                      className="text-success"
                                      style={{ fontSize: "14px" }}
                                    >
                                      <MarkdownRender variant="tableCell">
                                        {answer}
                                      </MarkdownRender>
                                    </div>
                                    {entryIndex <
                                      Object.entries(item).length - 1 && (
                                      <hr
                                        className="my-2"
                                        style={{ opacity: 0.3 }}
                                      />
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          ) : typeof item === "string" ? (
                            <MarkdownRender variant="tableCellPrimary">
                              {item}
                            </MarkdownRender>
                          ) : (
                            <pre className="mb-0 small text-wrap">
                              {JSON.stringify(item, null, 2)}
                            </pre>
                          )}
                        </div>
                      </td>
                      <td>
                        <Button
                          variant={
                            copiedRowIndex === index
                              ? "success"
                              : "outline-primary"
                          }
                          size="sm"
                          onClick={() => {
                            let textToCopy;
                            if (
                              typeof item === "object" &&
                              item !== null &&
                              !Array.isArray(item)
                            ) {
                              // Handle object with question as key and answer as value
                              const entries = Object.entries(item);
                              textToCopy = entries
                                .map(
                                  ([question, answer]) =>
                                    `Question: ${question}\n\nAnswer: ${answer}`
                                )
                                .join("\n\n---\n\n");
                            } else if (typeof item === "string") {
                              textToCopy = item;
                            } else {
                              textToCopy = JSON.stringify(item, null, 2);
                            }

                            navigator.clipboard.writeText(textToCopy);
                            setCopiedRowIndex(index);
                            setShowCopyNotification(true);

                            // Reset the copied row indicator after 2 seconds
                            setTimeout(() => {
                              setCopiedRowIndex(null);
                              setShowCopyNotification(false);
                            }, 2000);
                          }}
                          className="px-2"
                        >
                          <i
                            className={`fas ${
                              copiedRowIndex === index ? "fa-check" : "fa-copy"
                            } me-1`}
                          ></i>
                          {copiedRowIndex === index ? "Copied!" : "Copy"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />

      {/* Guide Flip Panel */}
      <GuideFlip
        show={showGuideFlip}
        onClose={() => setShowGuideFlip(false)}
        guideType="url"
      />
    </div>
  );
}
